import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredArticles = activeCategory === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  const currentCategoryName = activeCategory === 'all'
    ? t('category.allArticles')
    : categories.find((c) => c.slug === activeCategory)?.name || '';

  const breadcrumbItems = [
    { label: t('common.breadcrumbHome'), href: '/' },
    { label: currentCategoryName || t('category.title') },
  ];

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: currentCategoryName ? `${currentCategoryName} - ${t('common.siteTitle')}` : t('category.title'),
          description: `浏览${currentCategoryName}相关的深度文章，了解重组家庭中继母与继姐相处技巧、心理调适方法及关系修复策略。`,
          inLanguage: 'zh-CN',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.label,
              item: item.href ? `https://family-relationships-guide.com${item.href}` : undefined,
            })),
          },
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              {t('category.title')}
            </h1>

            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/category/all"
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-700 hover:bg-background-200 border border-background-200'
                }`}
              >
                {t('category.allArticles')}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <p className="text-sm text-foreground-500 mb-6">
              共 {filteredArticles.length} 篇文章
            </p>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => {
                  const cat = categories.find((c) => c.slug === article.category);
                  return (
                    <ArticleCard
                      key={article.id}
                      id={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      category={article.category}
                      author={article.author}
                      date={article.date}
                      readTime={article.readTime}
                      image={article.image}
                      categoryName={cat?.name}
                      categorySlug={cat?.slug}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}